import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  resolveContextFromPath,
  getContextConfig,
} from '@/app/providers/contextRegistry';
import { useContextStore } from '@/shared/stores/contextStore';
import type { AppContextType, AppContextConfig } from '@/app/providers/AppContext.types';

/**
 * useActiveContext
 *
 * Single source of truth for the active application context.
 *
 * ## How it works
 *
 * Active context is derived **purely from the URL pathname**:
 * - `/workplace/*` → 'workplace'
 * - `/code/*`      → 'code'
 * - `/admin/*`     → 'admin'
 * - anything else  → 'home'
 *
 * When switching contexts (via `setActiveContext`), the hook:
 * 1. Persists the choice to localStorage (via contextStore)
 * 2. Navigates to the context's defaultRoute
 *
 * ## Important
 * Components MUST use this hook to determine the active context.
 * Do NOT call `useLocation()` directly for context detection.
 *
 * @returns {activeContext} - the current context ID
 * @returns {setActiveContext} - switch to a different context
 * @returns {activeContextConfig} - full config for active context
 */
export function useActiveContext(): {
  activeContext: AppContextType;
  setActiveContext: (ctx: AppContextType) => void;
  activeContextConfig: AppContextConfig;
} {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { setLastSelectedContext } = useContextStore();

  // URL is the source of truth
  const activeContext = resolveContextFromPath(pathname);

  const setActiveContext = useCallback(
    (ctx: AppContextType) => {
      // Persist user's explicit selection
      setLastSelectedContext(ctx);
      // Navigate to context's default route
      const config = getContextConfig(ctx);
      navigate(config.defaultRoute);
    },
    [navigate, setLastSelectedContext]
  );

  return {
    activeContext,
    setActiveContext,
    activeContextConfig: getContextConfig(activeContext),
  };
}
